import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Toolbar, FormControlLabel, Switch } from '@mui/material';
import { getDateFormat, getListFilterLocalizedStrings } from 'utils/functions';
import { useDispatch } from 'react-redux';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import * as partnerActions from 'features/offer/edit/client/form/actionCreators';
import useDebounce from 'utils/hooks/useDebounce';
import documentTypeIds from 'constants/documentTypeIds';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import ListFilter from '@ts-private/list-filter';
import { debounce } from 'lodash';

const DEBOUNCE_DELAY = 500;

export const PartnerFileListTopToolbar = ({
  filters,
  downloadEnabled,
  visibleFilters,
  visibleFields,
  userPreferences,
  onUserPreferenceChanged,
  selectedUserPreference,
  onAddUserPreference,
  onDeleteUserPreference,
  onListDeleteChanged,
  onListImplicitChanged,
  onResetFilters,
  onFilterValueChanged,
  onFilterVisibleValueChanged,
  onFieldsVisibleValueChanged,
  onDownload
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [historySearchUsers, setHistorySearchUsers] = useState([]);
  const [searchUserInput, setSearchUserInput] = useState();
  const searchUserInputDebounced = useDebounce(searchUserInput, DEBOUNCE_DELAY);

  const handleChangeSearchUser = input => setSearchUserInput(input?.target?.value);
  const loadUsers = async () => {
    if (!searchUserInputDebounced) return [];

    let users = historySearchUsers.filter(u => u.userName.toLowerCase().includes(searchUserInputDebounced.toLowerCase()));
    if (users.length !== 0) return users;

    users = await dispatch(dictionariesActions.loadUsers(searchUserInputDebounced));
    setHistorySearchUsers(prev => [...prev, ...users]);
    return users;
  };

  const loadStates = useCallback(() => dispatch(dictionariesActions.loadStates(documentTypeIds.PartnerFile)), [dispatch]);
  const loadPartnerTypes = useCallback(() => dispatch(partnerActions.loadPartnerTypes()), [dispatch]);
  const loadVisibilityLevels = useCallback(() => dispatch(dictionariesActions.loadPartnerFileVisibilityLevels()), [dispatch]);

  const handleVisibleFilterChange = name => event => onFilterVisibleValueChanged(name, event.target.checked);
  const handleVisibleFieldsChange = name => event => onFieldsVisibleValueChanged(name, event.target.checked);

  const filtersToShow = visibleFilters.filter(x => x.isVisible).map(x => x.fieldName);

  const sortableColumns = [
    {
      columnName: 'SortableFileNumber',
      displayName: t('PartnerFileList.FileNumber')
    }
  ];

  const getSortableColumns = () => {
    return selectedUserPreference.order?.orderByColumns?.length > 0
      ? selectedUserPreference.order.orderByColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        })
      : sortableColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        });
  };

  return (
    <div>
      <Toolbar>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ListFilter
              sortableColumns={getSortableColumns()}
              filters={filters}
              onResetFilters={onResetFilters}
              visibleFilters={visibleFilters}
              visibleFields={visibleFields}
              onChangeFilterValue={onFilterValueChanged}
              handleVisibleFilterChange={handleVisibleFilterChange}
              handleVisibleFieldsChange={handleVisibleFieldsChange}
              onDownload={onDownload}
              downloadEnabled={downloadEnabled}
              downloadButtonVisible={true}
              entityName={'PartnerFile'}
              visibleUserPreferences={true}
              userPreferences={userPreferences}
              searchPlaceholder={t('Placeholders.PartnerFileListSearch')}
              onUserPreferenceChanged={onUserPreferenceChanged}
              selectedUserPreference={selectedUserPreference}
              onAddUserPreference={onAddUserPreference}
              onListDeleteChanged={onListDeleteChanged}
              onListImplicitChanged={onListImplicitChanged}
              onDeleteUserPreference={onDeleteUserPreference}
              searchTextMaxLength={255}
              localizedStrings={getListFilterLocalizedStrings(t)}
            >
              <Grid container spacing={2}>
                {filtersToShow.includes('partnerName') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'partnerFileList_filters_partnerName'}
                      label={t('PartnerFileList.Partner')}
                      value={filters.partnerName || ''}
                      onChange={value => onFilterValueChanged('partnerName', value)}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('uniqueId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'partnerFileList_filters_uniqueId'}
                      label={t('PartnerFileList.UniqueId')}
                      value={filters.uniqueId || ''}
                      onChange={value => onFilterValueChanged('uniqueId', value)}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('fileNumber') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <TextField
                      id={'partnerFileList_filters_fileNumber'}
                      label={t('PartnerFileList.FileNumber')}
                      value={filters.fileNumber || ''}
                      onChange={value => onFilterValueChanged('fileNumber', value)}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('partnerTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'partnerFileList_filters_partnerTypeId'}
                      label={t('PartnerFileList.PartnerType')}
                      valueKey='partnerTypeId'
                      labelKey='partnerTypeName'
                      value={filters.partnerType}
                      onChange={value => onFilterValueChanged('partnerType', value)}
                      loadOptions={debounce(loadPartnerTypes, DEBOUNCE_DELAY, { leading: true })}
                      fullWidth
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('userId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'partnerFileList_filters_user'}
                      label={t('PartnerFileList.User')}
                      valueKey='userId'
                      labelKey='userName'
                      value={filters.user}
                      onInputChange={handleChangeSearchUser}
                      onChange={value => onFilterValueChanged('user', value)}
                      loadOptions={debounce(loadUsers, DEBOUNCE_DELAY, { leading: true })}
                      fullWidth
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('stateId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'partnerFileList_filters_state'}
                      label={t('PartnerFileList.State')}
                      valueKey='stateId'
                      value={filters.state}
                      onChange={value => onFilterValueChanged('state', value)}
                      loadOptions={debounce(loadStates, DEBOUNCE_DELAY, { leading: true })}
                      fullWidth
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('offerTypeId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'partnerFileList_filters_offerType'}
                      label={t('PartnerFileList.OfferType')}
                      valueKey='offerTypeId'
                      labelKey='offerTypeName'
                      value={filters.offerType}
                      onChange={value => onFilterValueChanged('offerType', value)}
                      //loadOptions={debounce(loadOfferTypes, DEBOUNCE_DELAY, { leading: true })}
                      fullWidth
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('startDate') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <DateTime
                      id={'partnerFileList_filters_startDate'}
                      value={filters.startDate}
                      label={t('PartnerFileList.StartDate')}
                      onChange={value => onFilterValueChanged('startDate', value)}
                      isClearable
                      format={getDateFormat()}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('endDate') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <DateTime
                      id={'partnerFileList_filters_endDate'}
                      value={filters.endDate}
                      label={t('PartnerFileList.EndDate')}
                      onChange={value => onFilterValueChanged('endDate', value)}
                      isClearable
                      format={getDateFormat()}
                    />
                  </Grid>
                )}
                {filtersToShow.includes('visibilityLevelId') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <Autocomplete
                      id={'partnerFileList_filters_visibilityLevel'}
                      label={t('PartnerFileList.VisibilityLevel')}
                      valueKey='visibilityLevelId'
                      value={filters.visibilityLevel}
                      onChange={value => onFilterValueChanged('visibilityLevel', value)}
                      loadOptions={debounce(loadVisibilityLevels, DEBOUNCE_DELAY, { leading: true })}
                      fullWidth
                      isClearable
                    />
                  </Grid>
                )}
                {filtersToShow.includes('isActive') && (
                  <Grid item xs={12} sm={6} md={6} lg={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          id={'partnerFileList_filters_isActive'}
                          checked={Boolean(filters.isActive)}
                          onChange={e => onFilterValueChanged('isActive', e.target.checked)}
                        />
                      }
                      label={t('PartnerFileList.IsActive')}
                    />
                  </Grid>
                )}
              </Grid>
            </ListFilter>
          </Grid>
        </Grid>
      </Toolbar>
    </div>
  );
};

PartnerFileListTopToolbar.propTypes = {
  filters: PropTypes.shape({
    fullTextFilter: PropTypes.string,
    partnerName: PropTypes.string,
    uniqueId: PropTypes.string,
    fileNumber: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    isActive: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    offerType: PropTypes.object,
    partnerType: PropTypes.object,
    state: PropTypes.object,
    visibilityLevel: PropTypes.object,
    user: PropTypes.object
  }),
  onFilterValueChanged: PropTypes.func.isRequired,
  onFilterVisibleValueChanged: PropTypes.func.isRequired,
  onFieldsVisibleValueChanged: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloadEnabled: PropTypes.bool.isRequired,
  visibleFilters: PropTypes.array.isRequired,
  visibleFields: PropTypes.array.isRequired,
  userPreferences: PropTypes.array,
  onUserPreferenceChanged: PropTypes.func,
  selectedUserPreference: PropTypes.object,
  onAddUserPreference: PropTypes.func,
  onDeleteUserPreference: PropTypes.func,
  onListDeleteChanged: PropTypes.func,
  onListImplicitChanged: PropTypes.func
};

export default PartnerFileListTopToolbar;
